/** Internal sub-components for Table — extracted to keep Table.tsx under 400 lines. */
import { memo, useContext, useState, type ReactNode } from "react";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { MinusIcon } from "@phosphor-icons/react/Minus";
import { CaretUpIcon } from "@phosphor-icons/react/CaretUp";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { CaretUpDownIcon } from "@phosphor-icons/react/CaretUpDown";
import { CaretRightIcon } from "@phosphor-icons/react/CaretRight";
import { Icon } from "../../Icons/Icon";
import { layerClass } from "../../Theme/layers";
import { Tooltip } from "../../Feedback/Tooltip";
import { ActionCell } from "../../Cells/ActionCell";
import { PageEdgeContext } from "../../PageLayout/Internal/PageEdgeContext";
import { PanelContext } from "../../PageLayout/PanelContext";
import { columnSizingStyle } from "../columnSizing";
import { TableHeaderContext } from "./TableHeaderContext";
import type { Column } from "../Table";
import { ColumnSpacingContext } from "./ColumnSpacingContext";

// When the table is at the viewport edge (Page fullBleed), the first and last
// cells get a 24px page gutter so their content aligns with the page header
// content above. Anywhere else (inside a Panel, default Page), the cells keep
// their compact padding.
const EDGE_GUTTER = "first:pl-6 last:pr-6";

// Inside a fullBleed Panel, cells use a constant 16px horizontal padding so
// their content aligns with the Panel header row (also px-4) at every viewport.
// Without a Panel, padding stays responsive (compact below 2xl, 16px at 2xl+).
const CELL_X = "px-1.5 2xl:px-4";
// The "wide" ColumnSpacing, for tables whose columns all hug: the gap between two columns is
// exactly this padding doubled, and the default leaves them reading as one column. Flat rather
// than the default's responsive step: a hugging table has no surplus width to give its columns
// room on a narrow screen, so shrinking the gaps there defeats the point. Kept modest because
// every pixel here is width the table cannot give back - too much and it outgrows the window.
const CELL_X_WIDE = "px-5";
// Inside a full-bleed panel the body supplies the px-6 gutter, so the first/last
// cell drop their outer edge padding — content (and the ledger rules) line up
// with the panel title at the gutter; inter-column padding stays via px-4.
const CELL_X_IN_PANEL = "px-4 first:pl-0 last:pr-0";
const CELL_X_IN_PANEL_WIDE = "px-5 first:pl-0 last:pr-0";

// The horizontal padding for one cell, given where the table sits and how widely it asked for its
// columns to be spaced.
function useCellX(): string {
  const insidePanel = useContext(PanelContext);
  const atPageEdge = useContext(PageEdgeContext);
  const wide = useContext(ColumnSpacingContext) === "wide";

  if (!insidePanel) return wide ? CELL_X_WIDE : CELL_X;
  if (atPageEdge) {
    return wide ? "px-5 first:pl-6 last:pr-6" : "px-4 first:pl-6 last:pr-6";
  }
  return wide ? CELL_X_IN_PANEL_WIDE : CELL_X_IN_PANEL;
}

// ── Selection Checkbox ─────────────────────────────────────────────

export function SelectionCheckbox_({
  checked,
  indeterminate,
  onChange,
  testId,
  disabled,
  disabledReason,
}: {
  checked: boolean;
  indeterminate?: boolean | undefined;
  onChange: () => void;
  testId?: string | undefined;
  disabled?: boolean | undefined;
  /** Why this row can't be selected. Shown on hover — never leave it unsaid. */
  disabledReason?: ReactNode;
}) {
  const box = (
    <button
      type="button"
      disabled={disabled === true}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled !== true) onChange();
      }}
      role="checkbox"
      aria-checked={indeterminate === true && !checked ? "mixed" : checked}
      data-testid={testId}
      className={`inline-flex h-4.5 w-4.5 items-center justify-center rounded border transition-colors ${
        disabled === true
          ? "cursor-not-allowed border-border bg-muted opacity-50"
          : checked || indeterminate
            ? "border-brand bg-brand text-white"
            : "border-border-strong bg-card hover:border-brand"
      }`}
    >
      {checked && <Icon icon={CheckIcon} size="xs" weight="bold" />}
      {indeterminate && !checked && (
        <Icon icon={MinusIcon} size="xs" weight="bold" />
      )}
    </button>
  );

  // A disabled checkbox with no explanation is the worst of both worlds — the
  // row looks broken rather than ineligible. Same rule as Button's
  // disabledMessage.
  return disabledReason !== undefined ? (
    <Tooltip content={disabledReason}>{box}</Tooltip>
  ) : (
    box
  );
}

// ── Header Cell ──────────────────────────────────────────────────

export function TableHeaderCell_({
  col,
  sortKey,
  sortDir,
  onSort,
}: {
  col: Column<unknown>;
  sortKey: string | undefined;
  sortDir: "asc" | "desc";
  onSort: (key: string) => void;
}) {
  const isSorted = sortKey === col.key;
  const sortable = col.sortable !== undefined || col.serverSortable === true;
  const atPageEdge = useContext(PageEdgeContext);
  const cellX = useCellX();
  return (
    <th
      key={col.key}
      style={columnSizingStyle(col, false)}
      data-testid={`table-header-${col.key}`}
      className={`${cellX} py-2 text-xs font-bold tracking-wide whitespace-nowrap text-foreground uppercase last:rounded-tr-md 2xl:py-3 ${atPageEdge ? EDGE_GUTTER : ""} ${
        col.align === "center"
          ? "text-center"
          : col.align === "right"
            ? "text-right"
            : "text-left"
      } ${sortable ? "group cursor-pointer transition-colors select-none hover:text-foreground" : ""}`}
      onClick={sortable ? () => onSort(col.key) : undefined}
    >
      <span className="inline-flex items-center gap-1">
        <TableHeaderContext.Provider value={true}>
          <span>{col.header}</span>
        </TableHeaderContext.Provider>
        {sortable &&
          (isSorted ? (
            <span className="text-brand-text">
              <Icon
                icon={sortDir === "asc" ? CaretUpIcon : CaretDownIcon}
                size="xs"
                weight="fill"
              />
            </span>
          ) : (
            <span className="text-faint group-hover:text-muted-foreground">
              <Icon icon={CaretUpDownIcon} size="xs" />
            </span>
          ))}
      </span>
      {col.subtitle && (
        <div className="mt-0.5 text-[10px] font-semibold tracking-wide text-content-muted normal-case">
          {col.subtitle}
        </div>
      )}
    </th>
  );
}

// ── Memoized Row ──────────────────────────────────────────────────

export type RowHighlightColor = "green" | "red" | "blue" | "orange";

const highlightStyles: Record<RowHighlightColor, string> = {
  green: "bg-green/8 ring-2 ring-green/20 ring-inset hover:bg-green/12",
  red: "bg-red/8 ring-2 ring-red/20 ring-inset hover:bg-red/12",
  blue: "bg-blue/8 ring-2 ring-blue/20 ring-inset hover:bg-blue/12",
  orange: "bg-orange/8 ring-2 ring-orange/20 ring-inset hover:bg-orange/12",
};

export interface TableRowProps_ {
  rowKey: string;
  row: unknown;
  columns: Column<unknown>[];
  highlightColor: RowHighlightColor | undefined;
  isChecked: boolean;
  /** Undefined means selectable. A reason means it isn't, and says why. */
  selectionDisabledReason?: ReactNode;
  selectable: boolean;
  /** Whether to render the rightmost slot (for actions and/or the gear). */
  showRightCol: boolean;
  /** When set, the rightmost cell renders the action button(s) wrapped in
   *  ActionCell. When undefined (along with rowMenu), the cell renders
   *  empty (acts as a spacer for the configurable gear column). */
  rowActions: ((row: unknown) => ReactNode) | undefined;
  rowMenu: ((row: unknown) => ReactNode) | undefined;
  onSelectRow: (key: string) => void;
  isExpanded: boolean;
  onToggleExpand: (key: string) => void;
  /** When set, the row is expandable: it gets a trailing chevron cell and a
   *  full-width detail row rendering this content. */
  expandedRowRender: ((row: unknown) => ReactNode) | undefined;
}

export const TableRow_ = memo(
  // oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
  function TableRow_({
    rowKey,
    row,
    columns,
    highlightColor,
    isChecked,
    selectionDisabledReason,
    selectable,
    showRightCol,
    rowActions,
    rowMenu,
    onSelectRow,
    isExpanded,
    onToggleExpand,
    expandedRowRender,
  }: TableRowProps_) {
    const atPageEdge = useContext(PageEdgeContext);
    const insidePanel = useContext(PanelContext);
    const cellX = useCellX();
    const expandable = expandedRowRender !== undefined;
    const totalColSpan =
      columns.length +
      (selectable ? 1 : 0) +
      (showRightCol ? 1 : 0) +
      (expandable ? 1 : 0);
    // Lazy-mount-and-persist for the detail content: nothing renders until the
    // row is first expanded (content mounts in the same commit as the expand,
    // so the open animation still works), then it stays mounted so the close
    // animation has content to collapse over.
    const [hasBeenExpanded, setHasBeenExpanded] = useState(false);
    if (isExpanded && !hasBeenExpanded) setHasBeenExpanded(true);
    // Expandable tables always render a detail row beneath each row; that
    // detail row carries the separator (so the border sits at the bottom of the
    // main+detail pair), leaving the main row borderless. Non-expandable tables
    // keep the exact original class order (no snapshot churn).
    const rowBase = expandable
      ? "transition-colors"
      : "border-b border-border transition-colors last:border-b-0";
    return (
      <>
        <tr
          data-testid={`table-row-${rowKey}`}
          className={`${rowBase} ${
            highlightColor
              ? highlightStyles[highlightColor]
              : isChecked
                ? "bg-brand/8 hover:bg-brand/12"
                : "hover:bg-brand-subtle"
          }`}
        >
          {selectable && (
            <td
              className={`w-10 ${cellX} py-2 text-center 2xl:py-4 ${atPageEdge ? "first:pl-6" : ""}`}
            >
              <SelectionCheckbox_
                checked={isChecked}
                onChange={() => onSelectRow(rowKey)}
                testId={`table-select-row-${rowKey}`}
                disabled={selectionDisabledReason !== undefined}
                disabledReason={selectionDisabledReason}
              />
            </td>
          )}
          {columns.map((col) => (
            <td
              key={col.key}
              data-testid={`table-cell-${col.key}-${rowKey}`}
              style={columnSizingStyle(col, true)}
              className={`${cellX} py-2 2xl:py-4 ${atPageEdge ? EDGE_GUTTER : ""} ${col.width ? "overflow-hidden" : ""} ${
                col.align === "center"
                  ? "text-center"
                  : col.align === "right"
                    ? "text-right"
                    : "text-left"
              }`}
            >
              {col.render(row)}
            </td>
          ))}
          {showRightCol && (
            <td
              className={`${rowActions || rowMenu ? "" : "w-10"} ${cellX} py-2 text-right 2xl:py-4 ${atPageEdge ? "pr-6" : ""}`}
            >
              {(rowActions || rowMenu) && (
                <ActionCell
                  actions={rowActions?.(row)}
                  menu={rowMenu?.(row)}
                  data-testid={`table-row-menu-${rowKey}`}
                />
              )}
            </td>
          )}
          {expandable && (
            <td
              className={`w-10 ${cellX} py-2 text-right 2xl:py-4 ${atPageEdge ? "last:pr-6" : ""}`}
            >
              <button
                type="button"
                data-testid={`table-expand-row-${rowKey}`}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse row" : "Expand row"}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(rowKey);
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-brand-subtle hover:text-foreground focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
              >
                <span
                  className={`inline-flex transition-transform duration-150 ${isExpanded ? "rotate-90" : ""}`}
                >
                  <Icon icon={CaretRightIcon} size="sm" weight="bold" />
                </span>
              </button>
            </td>
          )}
        </tr>
        {expandable && (
          // The wrapper row is always mounted so open AND close animate. The
          // grid-rows 0fr↔1fr + overflow-hidden trick (same as
          // SelectionBanner_) animates height; collapsed, the row is 0-height
          // and its border reads as the normal row separator. The content
          // itself lazy-mounts on first expand and then persists (see
          // hasBeenExpanded above).
          <tr
            data-testid={`table-row-detail-${rowKey}`}
            className="border-b border-border last:border-b-0"
          >
            <td colSpan={totalColSpan} className="p-0">
              <div
                className="grid transition-[grid-template-rows] duration-200 ease-in-out motion-reduce:transition-none"
                style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
              >
                <div className="overflow-hidden">
                  <div
                    className={`bg-brand/[0.02] pb-4 ${atPageEdge ? "px-6" : insidePanel ? "px-4" : "px-2.5 2xl:px-4"}`}
                  >
                    {hasBeenExpanded ? expandedRowRender(row) : undefined}
                  </div>
                </div>
              </div>
            </td>
          </tr>
        )}
      </>
    );
  },
  (prev, next) =>
    prev.rowKey === next.rowKey &&
    prev.row === next.row &&
    prev.highlightColor === next.highlightColor &&
    prev.isChecked === next.isChecked &&
    prev.columns === next.columns &&
    prev.rowActions === next.rowActions &&
    prev.rowMenu === next.rowMenu &&
    prev.showRightCol === next.showRightCol &&
    prev.isExpanded === next.isExpanded &&
    prev.expandedRowRender === next.expandedRowRender,
);

// ── Selection Banner (select all rows across pages) ──────────────

export function SelectionBanner_({
  visible,
  selectedCount,
  totalRowCount,
  allDataSelected,
  hasMoreData,
  onSelectAllData,
  onClearSelection,
  actions,
  sticky = false,
}: {
  visible: boolean;
  selectedCount: number;
  totalRowCount: number;
  allDataSelected: boolean;
  hasMoreData: boolean;
  onSelectAllData: () => void;
  onClearSelection: () => void;
  actions?: ReactNode;
  /** Pin to the top of the window while scrolling. Opaque, since rows pass under it. */
  sticky?: boolean;
}) {
  return (
    <div
      data-testid="table-selection-banner"
      className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${sticky ? `sticky top-0 ${layerClass.raised} bg-card` : ""}`}
      style={{
        gridTemplateRows: visible ? "1fr" : "0fr",
        opacity: visible ? 1 : 0,
      }}
    >
      <div className="overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-brand/15 bg-brand/[0.03] px-4 py-2 text-sm text-content-secondary">
          <div className="flex items-center gap-2">
            {allDataSelected && hasMoreData ? (
              <>
                <span>All {totalRowCount} rows are selected.</span>
                <button
                  type="button"
                  data-testid="table-clear-selection"
                  className="font-medium text-brand-text underline hover:text-brand-text/80"
                  onClick={onClearSelection}
                >
                  Clear selection
                </button>
              </>
            ) : (
              <>
                <span>
                  {selectedCount} of {totalRowCount} rows selected.
                </span>
                {hasMoreData && (
                  <button
                    type="button"
                    data-testid="table-select-all-data"
                    className="font-medium text-brand-text underline hover:text-brand-text/80"
                    onClick={onSelectAllData}
                  >
                    Select all {totalRowCount} rows
                  </button>
                )}
                <button
                  type="button"
                  data-testid="table-clear-selection"
                  className="font-medium text-brand-text underline hover:text-brand-text/80"
                  onClick={onClearSelection}
                >
                  Clear selection
                </button>
              </>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
